import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import BoxGrid from "../../components/BoxGrid"
import CalloutBanner from "../../components/CalloutBanner"
import ButtonLink from "../../components/ButtonLink"
import Emoji from "../../components/Emoji"
import Translation from "../../components/Translation"
import { translateMessageId } from "../../utils/translations"
import { Page, Content, Divider } from "../../components/SharedStyledComponents"

const H2 = styled.h2`
  margin-top: 3rem;
  margin-bottom: 2rem;
`

const Text = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text200};
  max-width: 800px;
  margin-bottom: 2rem;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 4rem;
  @media (max-width: ${(props) => props.theme.breakpoints.s}) {
    flex-direction: column;
  }
`

const StyledCalloutBanner = styled(CalloutBanner)`
  margin: 4rem 0;
  .gatsby-image-wrapper {
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`

const PMOManagerPage = ({ data }) => {
  const intl = useIntl()

  const heroContent = {
    title: translateMessageId("page-pmo-manager-hero-title", intl),
    header: translateMessageId("page-pmo-manager-hero-header", intl),
    subtitle: translateMessageId("page-pmo-manager-hero-subtitle", intl),
    image: data.hero.childImageSharp.fluid,
    alt: "PMO Manager Dashboard",
    buttons: [
      {
        content: translateMessageId("page-pmo-manager-btn-app", intl),
        path: "https://pmo.newinntech.com",
      },
      {
        content: translateMessageId("page-pmo-manager-btn-docs", intl),
        path: "https://pmo-docs.newinntech.com/",
        isSecondary: "isSecondary",
      },
    ],
  }

  const features = [
    {
      title: translateMessageId("page-pmo-manager-feature-title-1", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-1", intl),
      emoji: ":zap:",
    },
    {
      title: translateMessageId("page-pmo-manager-feature-title-2", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-2", intl),
      emoji: ":stopwatch:",
    },
    {
      title: translateMessageId("page-pmo-manager-feature-title-3", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-3", intl),
      emoji: ":card_index_dividers:",
    },
    {
      title: translateMessageId("page-pmo-manager-feature-title-4", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-4", intl),
      emoji: ":calendar:",
    },
    {
      title: translateMessageId("page-pmo-manager-feature-title-5", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-5", intl),
      emoji: ":chart_with_upwards_trend:",
    },
    {
      title: translateMessageId("page-pmo-manager-feature-title-6", intl),
      description: translateMessageId("page-pmo-manager-feature-desc-6", intl),
      emoji: ":mag_right:",
    },
  ]

  return (
    <Page>
      <PageMetadata
        title="PMO Manager"
        description="Manual de usuario y documentación del aplicativo PMO Manager de NewInntech."
      />
      <PageHero content={heroContent} />

      <Content>
        <H2>
          <Translation id="page-pmo-manager-section-title" />{" "}
          <Emoji text=":+1:" size={1} />
        </H2>
        <Text>
          <Translation id="page-pmo-manager-section-desc" />
        </Text>

        <BoxGrid items={features} />

        <Divider />

        <StyledCalloutBanner
          title={translateMessageId("page-pmo-manager-callout-title", intl)}
          description={translateMessageId(
            "page-pmo-manager-callout-desc",
            intl
          )}
          image={data.callout.childImageSharp.fluid}
          alt="PMO Manager Teamwork"
          maxImageWidth={450}
        >
          <ButtonRow>
            <ButtonLink to="https://pmo.newinntech.com">
              <Translation id="page-pmo-manager-btn-app-secondary" />
            </ButtonLink>
            <ButtonLink
              isSecondary
              to="https://pmo-docs.newinntech.com/guide.html"
            >
              <Translation id="page-pmo-manager-btn-docs-secondary" />
            </ButtonLink>
          </ButtonRow>
        </StyledCalloutBanner>
      </Content>
    </Page>
  )
}

export default PMOManagerPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "pmo_manager_hero.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    callout: file(relativePath: { eq: "pmo_manager_callout.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
